import React, { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Megaphone, Plus, Send, Eye, Edit3, Trash2, AlertTriangle, Clock, Users,
} from 'lucide-react';
import { toast } from 'sonner';

const AUDIENCE_LABEL = {
  all: 'All active users',
  candidates: 'Candidates only',
  employers: 'Employers only (client_admin, team_leader, recruiter)',
};

const emptyForm = { name: '', subject: '', html_body: '', audience: 'all' };

const CustomBroadcastsSection = ({ token, masterEnabled }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [sendStatus, setSendStatus] = useState(null);
  const [sendStarting, setSendStarting] = useState(false);
  const [onlyNew, setOnlyNew] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/custom-broadcasts`, { headers, withCredentials: true });
      setItems(res.data.broadcasts || []);
    } catch { toast.error('Failed to load custom broadcasts'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openCompose = (existing = null) => {
    if (existing) {
      setForm({
        name: existing.name,
        subject: existing.subject,
        html_body: existing.html_body,
        audience: existing.audience || 'all',
      });
      setEditingId(existing.broadcast_id);
    } else {
      setForm(emptyForm);
      setEditingId(null);
    }
    setComposeOpen(true);
  };

  const saveBroadcast = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.html_body.trim()) {
      toast.error('Name, subject and body are required'); return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/custom-broadcasts/${editingId}`, form, { headers, withCredentials: true });
        toast.success('Broadcast updated');
      } else {
        await axios.post(`${API}/admin/custom-broadcasts`, form, { headers, withCredentials: true });
        toast.success('Broadcast saved as draft');
      }
      setComposeOpen(false);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const deleteBroadcast = async (b) => {
    if (!window.confirm(`Delete "${b.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/custom-broadcasts/${b.broadcast_id}`, { headers, withCredentials: true });
      toast.success('Deleted');
      fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const previewBroadcast = async (b) => {
    try {
      const res = await axios.get(`${API}/admin/custom-broadcasts/${b.broadcast_id}/preview`, { headers, withCredentials: true });
      setPreviewHtml(res.data.html);
      setPreviewSubject(res.data.subject);
      setPreviewOpen(true);
    } catch { toast.error('Preview failed'); }
  };

  const sendTest = async (b) => {
    setTesting(true);
    try {
      const payload = testEmail ? { to_email: testEmail } : {};
      const res = await axios.post(`${API}/admin/custom-broadcasts/${b.broadcast_id}/test`, payload, { headers, withCredentials: true });
      if (res.data.result?.sent) toast.success(`Test sent to ${res.data.to}`);
      else toast.info(`Not sent: ${res.data.result?.reason?.replace(/_/g, ' ')}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    setTesting(false);
  };

  const openSend = async (b) => {
    setSendTarget(b);
    setSendOpen(true);
    setSendStatus(null);
    try {
      const res = await axios.get(`${API}/admin/custom-broadcasts/${b.broadcast_id}/status`, { headers, withCredentials: true });
      setSendStatus(res.data);
    } catch { toast.error('Failed to load status'); }
  };

  const refreshSendStatus = async () => {
    if (!sendTarget) return;
    try {
      const res = await axios.get(`${API}/admin/custom-broadcasts/${sendTarget.broadcast_id}/status`, { headers, withCredentials: true });
      setSendStatus(res.data);
    } catch { /* silent */ }
  };

  const startSend = async () => {
    if (!sendTarget) return;
    setSendStarting(true);
    try {
      await axios.post(
        `${API}/admin/custom-broadcasts/${sendTarget.broadcast_id}/send`,
        { only_never_sent: onlyNew },
        { headers, withCredentials: true },
      );
      toast.success('Broadcast started in background');
      setTimeout(refreshSendStatus, 1500);
      setTimeout(refreshSendStatus, 5000);
      setTimeout(() => { refreshSendStatus(); fetchList(); }, 15000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start');
    }
    setSendStarting(false);
  };

  return (
    <div className="space-y-4" data-testid="custom-broadcasts-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Custom Broadcasts</h3>
          <p className="text-sm text-slate-500 mt-0.5">Compose one-time announcements without a code deploy</p>
        </div>
        <Button onClick={() => openCompose()} data-testid="compose-broadcast-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Compose Broadcast
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-sm text-slate-500">
            <Megaphone className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No custom broadcasts yet. Click "Compose Broadcast" to create your first one.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map(b => {
            const running = b.last_run?.status === 'running';
            const sent = b.status === 'sent';
            return (
              <Card key={b.broadcast_id} data-testid={`custom-bc-${b.broadcast_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                      <Megaphone className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="font-semibold text-slate-900 text-sm">{b.name}</p>
                        {running && <Badge className="bg-blue-100 text-blue-700 text-[10px]"><Clock className="w-2.5 h-2.5 mr-1" />Running</Badge>}
                        {sent && !running && <Badge className="bg-green-100 text-green-700 text-[10px]">Sent</Badge>}
                        {!sent && !running && <Badge className="bg-slate-100 text-slate-600 text-[10px]">Draft</Badge>}
                        <Badge variant="outline" className="text-[10px]">
                          <Users className="w-2.5 h-2.5 mr-1" />
                          {AUDIENCE_LABEL[b.audience || 'all'].split(' ')[0]}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mb-1 truncate">Subject: {b.subject}</p>
                      {b.last_run && (
                        <p className="text-[11px] text-slate-400">
                          Last run: sent {b.last_run.sent} · skipped {b.last_run.skipped} · failed {b.last_run.failed} · total {b.last_run.total}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => previewBroadcast(b)} className="h-8 px-2" data-testid={`bc-preview-${b.broadcast_id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openCompose(b)} className="h-8 px-2" data-testid={`bc-edit-${b.broadcast_id}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openSend(b)} className="h-8 px-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid={`bc-send-${b.broadcast_id}`}>
                        <Send className="w-3.5 h-3.5 mr-1" /> Send
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteBroadcast(b)} className="h-8 px-2 text-red-500 hover:bg-red-50" data-testid={`bc-del-${b.broadcast_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="compose-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>
              {editingId ? 'Edit Broadcast' : 'Compose New Broadcast'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto pr-1">
            <div>
              <Label className="text-xs text-slate-600">Internal Name</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Diwali greetings 2026"
                className="mt-1"
                data-testid="compose-name"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Email Subject</Label>
              <Input
                value={form.subject}
                onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="e.g., A special message for you, {{first_name}}"
                className="mt-1"
                data-testid="compose-subject"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Audience</Label>
              <Select value={form.audience} onValueChange={v => setForm(f => ({ ...f, audience: v }))}>
                <SelectTrigger className="mt-1" data-testid="compose-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All active users</SelectItem>
                  <SelectItem value="candidates">Candidates only</SelectItem>
                  <SelectItem value="employers">Employers only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Email Body (HTML supported)</Label>
              <Textarea
                value={form.html_body}
                onChange={e => setForm(f => ({ ...f, html_body: e.target.value }))}
                placeholder={'Hi {{first_name}},\n\n<p>Your update goes here.</p>\n\n<a href="https://edassist.co.in/jobs" style="display:inline-block;background:#4f46e5;color:#fff;padding:10px 20px;border-radius:8px;text-decoration:none;">View Jobs</a>'}
                rows={12}
                className="mt-1 font-mono text-xs"
                data-testid="compose-body"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Merge tags: <code className="bg-slate-100 px-1 rounded">{'{{first_name}}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{{name}}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{{email}}'}</code>. Your content is auto-wrapped in the EdAssist branded email layout.
              </p>
            </div>
          </div>
          <DialogFooter className="pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setComposeOpen(false)}>Cancel</Button>
            <Button onClick={saveBroadcast} disabled={saving} data-testid="compose-save">
              {saving ? 'Saving...' : editingId ? 'Update Draft' : 'Save Draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Preview: {previewSubject}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto border rounded-lg bg-slate-100 p-2" style={{ maxHeight: 'calc(85vh - 100px)' }}>
            <iframe srcDoc={previewHtml} title="Broadcast Preview" className="w-full bg-white rounded" style={{ height: '600px', border: 'none' }} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-lg" data-testid="send-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Send className="w-5 h-5 text-indigo-600" /> Send: {sendTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {sendStatus ? (
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-indigo-700">{sendStatus.total_recipients}</p>
                  <p className="text-[10px] uppercase tracking-wide text-indigo-600 mt-1">Eligible</p>
                </div>
                <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{sendStatus.already_sent}</p>
                  <p className="text-[10px] uppercase tracking-wide text-green-600 mt-1">Already Sent</p>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{Math.max(0, sendStatus.total_recipients - sendStatus.already_sent)}</p>
                  <p className="text-[10px] uppercase tracking-wide text-amber-600 mt-1">Remaining</p>
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500">Loading...</div>
            )}

            <div className="text-xs text-slate-500">
              Audience: <strong className="text-slate-700">{AUDIENCE_LABEL[sendTarget?.audience || 'all']}</strong>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <Switch checked={onlyNew} onCheckedChange={setOnlyNew} data-testid="send-only-new" />
              <div>
                <p className="text-sm font-medium text-slate-800">Only send to users who haven't received this yet</p>
                <p className="text-xs text-slate-500 mt-0.5">Recommended — prevents duplicate sends.</p>
              </div>
            </div>

            {sendTarget && (
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Test email address (defaults to you)"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  className="flex-1"
                  data-testid="test-email-input"
                />
                <Button variant="outline" onClick={() => sendTest(sendTarget)} disabled={testing} data-testid="test-send-btn">
                  {testing ? 'Sending...' : 'Send Test'}
                </Button>
              </div>
            )}

            {!masterEnabled && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                Master email switch is OFF. Emails will be logged but not delivered until you turn it on.
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" onClick={refreshSendStatus}>Refresh</Button>
              <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
              <Button
                onClick={startSend}
                disabled={sendStarting || sendStatus?.last_run?.status === 'running'}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="send-broadcast-btn"
              >
                <Send className="w-4 h-4 mr-1.5" />
                {sendStarting
                  ? 'Starting...'
                  : sendStatus?.last_run?.status === 'running'
                    ? 'Running...'
                    : `Send to ${sendStatus ? Math.max(0, sendStatus.total_recipients - (onlyNew ? sendStatus.already_sent : 0)) : '...'} users`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomBroadcastsSection;
